'use client';

/**
 * GroupPresenceCluster — the group-chat "who's here" widget as it appears in the
 * top-right header row, immediately beside Share.
 *
 * Rendered by `FloatingHeaderControls`, not by the chat page, so the
 * collaborators and Share read as one cluster (the Miro treatment). It takes no
 * props: everything comes from the `groupPresence` slice, which the chat session
 * publishes. That indirection is deliberate — the header is global layout in a
 * sibling subtree from the chat page, so there is no prop path between them, and
 * bridging via a DOM portal raced the two subtrees' mount order.
 *
 * Renders nothing at all outside a group session, so every other page pays only
 * a cheap selector read.
 */

import { Users } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { GroupPresenceBar } from '@/components/chat/GroupPresenceBar';
import { LexInfoMenu } from '@/components/chat/LexInfoMenu';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { setMembersOpen } from '@/lib/redux/slices/groupPresenceSlice';
import { cn } from '@/lib/utils';

interface GroupPresenceClusterProps {
  /**
   * Drop the cluster's own pill chrome (border/background/shadow) so it can be
   * composed INSIDE another pill — how `ShareButton` merges collaborators and
   * Share into one control. Standalone, the chrome is what makes it read as a
   * deliberate widget rather than loose floating controls.
   */
  bare?: boolean;
}

export function GroupPresenceCluster({
  bare = false,
}: GroupPresenceClusterProps = {}): ReactElement | null {
  const t = useTranslations('ResearchPage');
  const dispatch = useAppDispatch();
  const { sessionId, presentUserIds, typingUserIds, selfUserId, aiActive } =
    useAppSelector((state) => state.groupPresence);

  // No group session in view — the header shows nothing.
  if (sessionId === null) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1.5',
        !bare &&
          'rounded-full border border-line-subtle bg-surface-card/80 py-1 pl-2 pr-1 shadow-sm backdrop-blur-sm'
      )}
    >
      {/* NOT gated on the socket being connected: the roster-driven stack must
          stay on screen across handshakes and reconnects. Gating it made the
          group icons blink out on every drop — the exact moment a member wants
          to see who is in the room. Live-vs-offline is per avatar, via its
          online badge. */}
      <GroupPresenceBar
        sessionId={sessionId}
        presentUserIds={presentUserIds}
        typingUserIds={typingUserIds}
        selfUserId={selfUserId ?? undefined}
        aiActive={aiActive}
      />
      <div className='h-5 w-px bg-line-subtle' aria-hidden='true' />
      <LexInfoMenu />
      <button
        type='button'
        onClick={() => dispatch(setMembersOpen(true))}
        aria-label={t('manageMembers')}
        className='flex h-8 w-8 items-center justify-center rounded-full text-brand-tertiary/70 transition-colors hover:bg-interactive-hover hover:text-brand-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary'
      >
        <Users className='h-4 w-4' aria-hidden='true' />
      </button>
    </div>
  );
}
